import type { ReactNode } from 'react';
import type { BookingStatus } from '../types';

export function Loader({ text = 'Загружаем...' }: { text?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-tg-hint">
      <div className="mb-2 h-6 w-6 animate-spin rounded-full border-2 border-tg-hint/40 border-t-tg-link" />
      <p className="text-sm">{text}</p>
    </div>
  );
}

export function EmptyState({ icon = '📭', title, hint }: { icon?: string; title: string; hint?: string }) {
  return (
    <div className="my-10 flex flex-col items-center justify-center px-4 text-center">
      <div className="mb-2 text-4xl">{icon}</div>
      <p className="font-semibold">{title}</p>
      {hint && <p className="mt-1 text-sm text-tg-hint">{hint}</p>}
    </div>
  );
}

export function ErrorBox({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="card border border-red-300/40 bg-red-500/10">
      <p className="text-sm text-red-300">{message}</p>
      {onRetry && (
        <button className="btn-secondary mt-2" onClick={onRetry}>
          Повторить
        </button>
      )}
    </div>
  );
}

export function PageHeader({ title, subtitle, action }: { title: string; subtitle?: string; action?: ReactNode }) {
  return (
    <header className="mb-4 flex items-start justify-between">
      <div>
        <h1 className="text-xl font-bold leading-tight">{title}</h1>
        {subtitle && <p className="text-sm text-tg-hint">{subtitle}</p>}
      </div>
      {action}
    </header>
  );
}

const STATUS_LABELS: Record<BookingStatus, string> = {
  pending: 'Ждёт подтверждения',
  confirmed: 'Подтверждена',
  tentative: 'Под вопросом',
  rejected: 'Отклонена',
  cancelled_by_client: 'Отменена клиентом',
  cancelled_by_master: 'Отменена мастером',
  completed: 'Завершена',
  no_show: 'Неявка',
};

const STATUS_STYLES: Record<BookingStatus, string> = {
  pending: 'bg-yellow-500/15 text-yellow-300',
  confirmed: 'bg-green-500/15 text-green-300',
  tentative: 'bg-orange-500/15 text-orange-300',
  rejected: 'bg-red-500/15 text-red-300',
  cancelled_by_client: 'bg-gray-500/15 text-gray-300',
  cancelled_by_master: 'bg-gray-500/15 text-gray-300',
  completed: 'bg-blue-500/15 text-blue-300',
  no_show: 'bg-red-500/15 text-red-300',
};

export function StatusBadge({ status }: { status: BookingStatus }) {
  return <span className={`badge ${STATUS_STYLES[status]}`}>{STATUS_LABELS[status]}</span>;
}

export function formatDateTime(iso: string, opts?: { dateOnly?: boolean; timeOnly?: boolean }): string {
  const d = new Date(iso);
  const dateStr = d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' });
  const timeStr = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  if (opts?.dateOnly) return dateStr;
  if (opts?.timeOnly) return timeStr;
  return `${dateStr}, ${timeStr}`;
}

export function formatPrice(min: string, max: string | null): string {
  const m = Number(min).toLocaleString('ru-RU');
  if (max && Number(max) !== Number(min)) {
    return `${m}–${Number(max).toLocaleString('ru-RU')} ₽`;
  }
  return `${m} ₽`;
}

export function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h && m) return `${h} ч ${m} мин`;
  if (h) return `${h} ч`;
  return `${m} мин`;
}
